/**
 * Display helpers for the work-report detail page (D2f).
 *
 * Pure functions, no React, no fetching — so every rule below is
 * testable on its own and the page component stays an orchestrator
 * (CLAUDE.md Regel 48).
 *
 * The vocabulary the lists already use (REPORT_TYPE_LABELS,
 * formatWorkDate, isoToTimeInputValue) is imported rather than
 * reinvented, so a report reads the same in a row and on its own page.
 */
import { formatDateOnly } from "../../../utils/datetime";
import {
  REPORT_TYPE_LABELS,
  isoToTimeInputValue,
} from "../calendar/workReports/workReportConstants";
import { propertyEntry, roomEntry } from "../entityLinks/entityLinkEntries";

/**
 * The report's day as "05.08.2026".
 *
 * The lists use `formatWorkDate`, which prefixes the weekday
 * ("Mi., 05.08.2026") — useful when scanning many rows, noise on a page
 * about a single day, and not what the reference design shows. Same
 * reasoning as `formatDurationLong`: a second rendering, not a second
 * source of truth. `formatDateOnly` also splits a YYYY-MM-DD string
 * without constructing a Date, so the civil date the backend derived
 * cannot shift by a timezone.
 */
export function reportDateText(value) {
  return value ? formatDateOnly(value) : "—";
}

/**
 * Minutes → "45 Min.", "2 Std. 30 Min.", "12 Std.", "0 Min.".
 *
 * The lists use `formatMinutes` ("1h 30m"), which is right for a dense
 * table cell but wrong for a detail page, where the reference design
 * spells the unit out. Both live side by side on purpose: this is a
 * second RENDERING of the same server-owned number, not a second source
 * of truth, and changing `formatMinutes` would silently restyle every
 * existing list and its tests.
 *
 * A whole number of hours drops the "0 Min." tail — "12 Std.", not
 * "12 Std. 0 Min.".
 */
export function formatDurationLong(minutes) {
  const m = Number.isFinite(minutes) ? Math.max(0, Math.trunc(minutes)) : 0;
  const hours = Math.floor(m / 60);
  const rest = m % 60;
  if (hours === 0) return `${rest} Min.`;
  if (rest === 0) return `${hours} Std.`;
  return `${hours} Std. ${rest} Min.`;
}

/** "2 Stunden 30 Minuten" — spoken form, because "2 Std." is read out
 * as an abbreviation by screen readers. */
export function spokenDurationLong(minutes) {
  const m = Number.isFinite(minutes) ? Math.max(0, Math.trunc(minutes)) : 0;
  const hours = Math.floor(m / 60);
  const rest = m % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "Stunde" : "Stunden"}`);
  if (rest > 0 || hours === 0) {
    parts.push(`${rest} ${rest === 1 ? "Minute" : "Minuten"}`);
  }
  return parts.join(" ");
}

/**
 * "14:00–14:45", "Ab 14:00" or "Keine Zeitangabe".
 *
 * Both columns are NOT NULL in the schema, so in practice the first
 * form always wins. The other two are not dead weight: this same
 * function renders whatever the wire actually delivers, and a report
 * arriving without a parseable end must degrade to a sentence rather
 * than to "14:00–".
 */
export function timeRangeText(report) {
  const start = report?.started_at ? isoToTimeInputValue(report.started_at) : "";
  const end = report?.ended_at ? isoToTimeInputValue(report.ended_at) : "";
  if (start && end) return `${start}–${end}`;
  if (start) return `Ab ${start}`;
  if (end) return `Bis ${end}`;
  return "Keine Zeitangabe";
}

/** The report's headline. `title` is required by the schema, so it is
 * always the answer; the rest is a guard for a legacy empty string. */
export function reportTitle(report) {
  const title = (report?.title || "").trim();
  if (title) return title;
  const type = categoryLabel(report);
  if (type) return type;
  if (report?.work_date) return `Rapport vom ${formatDateOnly(report.work_date)}`;
  return "Rapport";
}

/** German category label, falling back to the raw value so an unknown
 * type is visible rather than blank. */
export function categoryLabel(report) {
  const type = report?.report_type;
  if (!type) return null;
  return REPORT_TYPE_LABELS[type] || type;
}

/** Full name → e-mail → honest placeholder. Never a UUID. */
export function assigneeText(report) {
  return (
    (report?.assigned_to_full_name || "").trim() ||
    (report?.assigned_to_email || "").trim() ||
    "Unbekannter Mitarbeiter"
  );
}

/** Full name → e-mail → "Nicht verfügbar". Never a UUID. */
export function creatorText(report) {
  return (
    (report?.created_by_full_name || "").trim() ||
    (report?.created_by_email || "").trim() ||
    "Nicht verfügbar"
  );
}

/**
 * The six links of a report, in the reference design's order.
 *
 * Each entry is built from the report's OWN foreign key. Nothing is
 * derived across entities — no tenant from a tenancy, no unit from a
 * tenancy, no landlord from a unit, no application from a task.
 *
 * `to` is null when the entity has no detail route in this app; the
 * card then prints the label without a chevron instead of inventing a
 * path. Tenancies are the current case: they are rendered inside the
 * tenant page as cards and have no route of their own.
 */
export function reportLinks(report) {
  const r = report || {};
  return [
    {
      key: "task",
      label: "Aufgabe",
      value: (r.task_title || "").trim() || (r.task_id ? "Aufgabe" : null),
      to: r.task_id ? `/admin/tasks/${r.task_id}` : null,
    },
    {
      key: "tenant",
      label: "Mieter",
      value:
        (r.tenant_display_name || "").trim() || (r.tenant_id ? "Mieter" : null),
      to: r.tenant_id ? `/admin/tenants/${r.tenant_id}` : null,
    },
    {
      key: "tenancy",
      label: "Mietverhältnis",
      value: tenancyLabel(r),
      // No /admin/tenancies/:id route exists — tenancies live as cards
      // on the tenant page. Label only, deliberately not a link.
      to: null,
    },
    // D4c1: Liegenschaft → Unit → Zimmer read as one object context, so
    // they sit together. All three remain INDEPENDENT direct links — a
    // room never contributes its own unit or property as a row, because
    // that would show a link the person never chose.
    propertyEntry(r),
    {
      key: "unit",
      label: "Unit",
      value: unitLabel(r),
      to: r.unit_id ? `/admin/units/${r.unit_id}` : null,
    },
    roomEntry(r),
    {
      key: "application",
      label: "Bewerbung",
      value:
        (r.application_display_name || "").trim() ||
        (r.application_id ? "Bewerbung" : null),
      to: r.application_id ? `/admin/applications/${r.application_id}` : null,
    },
    {
      key: "landlord",
      label: "Vermieter",
      value:
        (r.landlord_display_name || "").trim() ||
        (r.landlord_id ? "Vermieter" : null),
      to: r.landlord_id ? `/admin/landlords/${r.landlord_id}` : null,
    },
  ];
}

/** "FAHN-MV-2026-000015 · Madrid", either half alone, or a plain word
 * when the tenancy is linked but carries neither. */
export function tenancyLabel(report) {
  if (!report?.tenancy_id) return null;
  const parts = [
    (report.tenancy_reference_number || "").trim(),
    (report.tenancy_unit_short_id || "").trim(),
  ].filter(Boolean);
  return parts.join(" · ") || "Mietverhältnis";
}

/** "Madrid · 4-Zimmer-Wohnung", either half alone, else a plain word. */
export function unitLabel(report) {
  if (!report?.unit_id) return null;
  const parts = [
    (report.unit_short_id || "").trim(),
    (report.unit_title || "").trim(),
  ].filter(Boolean);
  return parts.join(" · ") || "Unit";
}
